// v1.17.3 — confirmation modal pra downgrade end-of-period.
// Lista features que o user vai perder + data de efeito + CTA que dispara
// cancel-subscription edge function (via useSubscription.cancelDowngrade).

import React, { useState } from 'react';
import { AlertTriangle, Loader2, Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../Button';
import type { PlanTier } from '../../lib/plans/types';

export interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tier atual (Pro ou Business). */
  currentTier: Exclude<PlanTier, 'free'>;
  /** Tier alvo do downgrade (Free ou Pro). */
  targetTier: 'free' | 'pro';
  /** ISO string do fim do ciclo atual. */
  periodEnd: string | null;
  /** Executa a mudança. Caller trata toast + state. */
  onConfirm: () => Promise<void>;
}

// Features perdidas por caminho de downgrade (matriz EPIC tier matrix).
// Somente combinações reais de downgrade (currentTier != targetTier &&
// targetTier < currentTier). Lookup via string key + fallback [].
const FEATURES_LOST: Record<string, string[]> = {
  'pro->free': [
    'Até 30 formulários ativos (você terá só 5)',
    'Até 800 respostas/mês (você terá 50)',
    'Webhooks (todas as integrações ativas)',
    'Upload de logo customizado',
    'Remoção do watermark "Powered by OneForm"',
    'Notificações por email + redirect URL',
    'Exportação individual em PDF',
    'Flow branching com condições ilimitadas',
  ],
  'business->pro': [
    'Respostas/mês ilimitadas (você terá 800)',
    'Formulários ilimitados (você terá 30)',
    'Webhooks ilimitados (você terá 3)',
    'Relatório analítico agregado (PDF com gráficos)',
    'Até 5 assentos de equipe',
  ],
  'business->free': [
    'Todos os recursos Business + Pro',
    'Retorno para limites Free: 5 forms / 50 respostas/mês',
  ],
};

const TIER_LABEL: Record<PlanTier, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
};

function formatDateBR(iso: string | null): string {
  if (!iso) return 'fim do ciclo atual';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export const DowngradeConfirmationModal: React.FC<DowngradeConfirmationModalProps> = ({
  isOpen,
  onClose,
  currentTier,
  targetTier,
  periodEnd,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = `${currentTier}->${targetTier}`;
  const featuresLost = FEATURES_LOST[key] ?? [];
  const accessUntil = formatDateBR(periodEnd);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      // Caller (BillingPanel) fecha o modal em caso de sucesso via onClose
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar downgrade');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      size="md"
      showCloseButton={!submitting}
      closeOnEscape={!submitting}
      closeOnOverlayClick={!submitting}
    >
      <div className="px-2 py-2">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              Fazer downgrade para {TIER_LABEL[targetTier]}?
            </h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              Mudança agendada — você não perde acesso agora.
            </p>
          </div>
        </div>

        {featuresLost.length > 0 && (
          <div className="mb-5">
            <p className="text-sm text-neutral-300 mb-3">
              Ao fazer downgrade do <strong className="text-white">{TIER_LABEL[currentTier]}</strong>{' '}
              para <strong className="text-white">{TIER_LABEL[targetTier]}</strong>, você perderá:
            </p>
            <ul className="space-y-2">
              {featuresLost.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                  <span className="text-amber-400 mt-0.5 shrink-0">•</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="p-3 rounded-lg bg-primary-500/5 border border-primary-500/20 text-sm text-neutral-300 mb-5">
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium mb-1">Mudança no fim do ciclo</p>
              <p className="text-xs text-neutral-400">
                Você mantém o plano <strong>{TIER_LABEL[currentTier]}</strong> até{' '}
                <strong className="text-white">{accessUntil}</strong>. Nenhuma cobrança adicional
                será feita. Pode cancelar esse downgrade a qualquer momento antes dessa data.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400"
          >
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-neutral-950 font-medium hover:bg-amber-400 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processando...
              </>
            ) : (
              'Confirmar downgrade'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
